"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_BRAND_VIDEO_SETTINGS = exports.BRAND_VIDEO_SETTINGS_SCHEMA = exports.BRAND_VIDEO_CAPTION_RENDER_SETTINGS_SCHEMA = exports.BRAND_VIDEO_CAPTION_CUSTOMIZATION_SCHEMA = exports.BRAND_VIDEO_TEXT_CUSTOMIZATION_SCHEMA = exports.BRAND_VIDEO_CAPTION_FONT_MAX_LENGTH = exports.BRAND_VIDEO_CAPTION_WEIGHTS = exports.BRAND_VIDEO_CAPTION_SHADOWS = exports.BRAND_VIDEO_CAPTION_POSITIONS = exports.BRAND_VIDEO_CAPTION_PRESET_DESCRIPTIONS = exports.BRAND_VIDEO_CAPTION_PRESETS = exports.BRAND_VIDEO_SETTING_MODES = void 0;
exports.getBrandVideoHighlightColor = getBrandVideoHighlightColor;
exports.normalizeBrandVideoSettings = normalizeBrandVideoSettings;
const zod_1 = require("zod");
exports.BRAND_VIDEO_SETTING_MODES = ['AUTO', 'ON', 'OFF'];
exports.BRAND_VIDEO_CAPTION_PRESETS = [
    'ali',
    'beans',
    'boo',
    'capri',
    'casper',
    'corpo',
    'diego',
    'flex',
    'hustle',
    'karl',
    'kitty',
    'lowkey',
    'mint',
    'plain',
    'rizz',
    'shadeplay',
    'simple',
    'slay',
    'sprout',
    'vegas',
    'vinta',
];
exports.BRAND_VIDEO_CAPTION_PRESET_DESCRIPTIONS = {
    ali: 'Bold black text inside a clean white rounded label.',
    beans: 'Oversized white all-caps text with a heavy creator-style impact.',
    boo: 'Minimal white text on a subtle dark translucent caption plate.',
    capri: 'Warm italic peach text with a softer lifestyle feel.',
    casper: 'Simple bold white text, centered and easy to read.',
    corpo: 'Polished bold white subtitles with a clean corporate tone.',
    diego: 'Punchy high-contrast creator captions with strong emphasis.',
    flex: 'Large athletic captions with energetic emphasis and motion.',
    hustle: 'Fast-paced bold creator captions built for direct-response hooks.',
    karl: 'Clean editorial captions with a measured, premium feel.',
    kitty: 'Veed-style playful rounded captions with a friendly social tone.',
    lowkey: 'Restrained low-contrast captions for subtle, quiet videos.',
    mint: 'Fresh modern captions with a light, crisp look.',
    plain: 'Unstyled readable captions that stay out of the way.',
    rizz: 'High-energy social captions with expressive emphasis.',
    shadeplay: 'White text on a dark shaded backing for busy footage.',
    simple: 'Classic bold white captions with predictable readability.',
    slay: 'Confident bold captions with a fashion-forward creator feel.',
    sprout: 'Soft rounded captions with an organic, approachable tone.',
    vegas: 'Bright entertainment-style captions with a louder promo feel.',
    vinta: 'Vintage-inspired captions with a slightly nostalgic look.',
};
exports.BRAND_VIDEO_CAPTION_POSITIONS = ['top', 'center', 'bottom'];
exports.BRAND_VIDEO_CAPTION_SHADOWS = ['none', 'min', 'mid', 'max'];
exports.BRAND_VIDEO_CAPTION_WEIGHTS = [100, 200, 300, 400, 500, 600, 700, 800, 900];
const HEX_COLOR_SCHEMA = zod_1.z.string().regex(/^#([0-9A-Fa-f]{3}){1,2}$/);
const FILE_UUID_SCHEMA = zod_1.z.string().uuid();
exports.BRAND_VIDEO_CAPTION_FONT_MAX_LENGTH = 128;
exports.BRAND_VIDEO_TEXT_CUSTOMIZATION_SCHEMA = zod_1.z
    .object({
    font: zod_1.z.string().trim().min(1).max(exports.BRAND_VIDEO_CAPTION_FONT_MAX_LENGTH).nullable().optional(),
    weight: zod_1.z.number().int().min(100).max(900).nullable().optional(),
    color: HEX_COLOR_SCHEMA.nullable().optional(),
})
    .strict();
exports.BRAND_VIDEO_CAPTION_CUSTOMIZATION_SCHEMA = zod_1.z
    .object({
    position: zod_1.z.enum(exports.BRAND_VIDEO_CAPTION_POSITIONS),
    shadow: zod_1.z.enum(exports.BRAND_VIDEO_CAPTION_SHADOWS),
    textCustomizations: zod_1.z
        .object({
        baseline: exports.BRAND_VIDEO_TEXT_CUSTOMIZATION_SCHEMA.optional(),
        highlighted: exports.BRAND_VIDEO_TEXT_CUSTOMIZATION_SCHEMA.optional(),
    })
        .strict(),
})
    .strict();
exports.BRAND_VIDEO_CAPTION_RENDER_SETTINGS_SCHEMA = zod_1.z
    .object({
    preset: zod_1.z.enum(exports.BRAND_VIDEO_CAPTION_PRESETS),
    customization: exports.BRAND_VIDEO_CAPTION_CUSTOMIZATION_SCHEMA,
})
    .strict();
exports.BRAND_VIDEO_SETTINGS_SCHEMA = zod_1.z
    .object({
    captions: zod_1.z
        .object({
        mode: zod_1.z.enum(exports.BRAND_VIDEO_SETTING_MODES),
        preset: exports.BRAND_VIDEO_CAPTION_RENDER_SETTINGS_SCHEMA.shape.preset,
        customization: exports.BRAND_VIDEO_CAPTION_CUSTOMIZATION_SCHEMA,
    })
        .strict(),
    endCard: zod_1.z
        .object({
        mode: zod_1.z.enum(exports.BRAND_VIDEO_SETTING_MODES),
    })
        .strict(),
    backgroundMusic: zod_1.z
        .object({
        fileUuid: FILE_UUID_SCHEMA.nullable(),
    })
        .strict(),
})
    .strict();
exports.DEFAULT_BRAND_VIDEO_SETTINGS = {
    captions: {
        mode: 'AUTO',
        preset: 'simple',
        customization: {
            position: 'bottom',
            shadow: 'none',
            textCustomizations: {},
        },
    },
    endCard: {
        mode: 'AUTO',
    },
    backgroundMusic: {
        fileUuid: null,
    },
};
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function parseEnumValue(values, value) {
    return typeof value === 'string' && values.includes(value) ? value : undefined;
}
function normalizeHexColor(value) {
    if (typeof value !== 'string')
        return undefined;
    const trimmed = value.trim();
    if (!HEX_COLOR_SCHEMA.safeParse(trimmed).success)
        return undefined;
    if (trimmed.length === 4) {
        const [, r, g, b] = trimmed;
        return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
    }
    return trimmed.toUpperCase();
}
function isWhiteHexColor(value) {
    return value === '#FFFFFF';
}
function getBrandVideoHighlightColor(brandColors) {
    if (!Array.isArray(brandColors))
        return undefined;
    for (const color of brandColors) {
        const normalizedColor = normalizeHexColor(color);
        if (normalizedColor && !isWhiteHexColor(normalizedColor)) {
            return normalizedColor;
        }
    }
    return undefined;
}
function normalizeTextCustomization(value) {
    if (!isRecord(value))
        return undefined;
    const parsed = exports.BRAND_VIDEO_TEXT_CUSTOMIZATION_SCHEMA.safeParse(value);
    if (!parsed.success)
        return undefined;
    const output = {};
    const font = parsed.data.font?.trim();
    if (font)
        output.font = font;
    if (typeof parsed.data.weight === 'number')
        output.weight = parsed.data.weight;
    if (parsed.data.color)
        output.color = parsed.data.color;
    return Object.keys(output).length > 0 ? output : undefined;
}
function normalizeBrandVideoSettings(value, options = {}) {
    const root = isRecord(value) ? value : {};
    const captions = isRecord(root.captions) ? root.captions : {};
    const endCard = isRecord(root.endCard) ? root.endCard : {};
    const backgroundMusic = isRecord(root.backgroundMusic) ? root.backgroundMusic : {};
    const customization = isRecord(captions.customization) ? captions.customization : {};
    const textCustomizations = isRecord(customization.textCustomizations) ? customization.textCustomizations : {};
    const baseline = normalizeTextCustomization(textCustomizations.baseline);
    const highlighted = normalizeTextCustomization(textCustomizations.highlighted);
    const brandHighlightColor = getBrandVideoHighlightColor(options.brandColors);
    const parsedBackgroundMusicFileUuid = FILE_UUID_SCHEMA.safeParse(backgroundMusic.fileUuid);
    const highlightedWithBrandColor = brandHighlightColor && !highlighted?.color
        ? {
            ...(highlighted ?? {}),
            color: brandHighlightColor,
        }
        : highlighted;
    return {
        captions: {
            mode: parseEnumValue(exports.BRAND_VIDEO_SETTING_MODES, captions.mode) ?? exports.DEFAULT_BRAND_VIDEO_SETTINGS.captions.mode,
            preset: parseEnumValue(exports.BRAND_VIDEO_CAPTION_PRESETS, captions.preset) ??
                exports.DEFAULT_BRAND_VIDEO_SETTINGS.captions.preset,
            customization: {
                position: parseEnumValue(exports.BRAND_VIDEO_CAPTION_POSITIONS, customization.position) ??
                    exports.DEFAULT_BRAND_VIDEO_SETTINGS.captions.customization.position,
                shadow: parseEnumValue(exports.BRAND_VIDEO_CAPTION_SHADOWS, customization.shadow) ??
                    exports.DEFAULT_BRAND_VIDEO_SETTINGS.captions.customization.shadow,
                textCustomizations: {
                    ...(baseline ? { baseline } : {}),
                    ...(highlightedWithBrandColor ? { highlighted: highlightedWithBrandColor } : {}),
                },
            },
        },
        endCard: {
            mode: parseEnumValue(exports.BRAND_VIDEO_SETTING_MODES, endCard.mode) ?? exports.DEFAULT_BRAND_VIDEO_SETTINGS.endCard.mode,
        },
        backgroundMusic: {
            fileUuid: parsedBackgroundMusicFileUuid.success ? parsedBackgroundMusicFileUuid.data : null,
        },
    };
}
